import React, { useEffect, useState } from 'react';
import { Box, Button, Grid, TextField, Typography, Alert } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../utils/api';

const API_BASE = API_BASE_URL;

const TransferHubProfile: React.FC = () => {
  const { token } = useAuth();
  const [form, setForm] = useState({
    profession: '',
    rank: '',
    department: '',
    currentLocation: '',
    preferredLocations: '' as any,
    qualifications: '',
    majors: '',
    cvUrl: ''
  });
  const [selectedCvFile, setSelectedCvFile] = useState<File | null>(null);
  const [cvPreview, setCvPreview] = useState<string>('');
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/transferhub/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data) {
            setForm({
              profession: data.profession || '',
              rank: data.rank || '',
              department: data.department || '',
              currentLocation: data.currentLocation || '',
              preferredLocations: Array.isArray(data.preferredLocations) ? data.preferredLocations.join(', ') : '',
              qualifications: data.qualifications || '',
              majors: data.majors || '',
              cvUrl: data.cvUrl || ''
            });
            setCvPreview(data.cvUrl || '');
          }
        }
      } catch {}
    };
    load();
  }, [token]);

  const handleSave = async () => {
    try {
      let finalCvUrl = form.cvUrl;
      
      // If a CV file is selected, upload it first
      if (selectedCvFile) {
        const formDataFile = new FormData();
        formDataFile.append('cv', selectedCvFile);
        
        const uploadResponse = await fetch(`${API_BASE}/api/upload/cv`, {
          method: 'POST',
          body: formDataFile,
        });
        
        if (uploadResponse.ok) {
          const uploadResult = await uploadResponse.json();
          finalCvUrl = uploadResult.cvUrl;
        } else {
          console.error('Failed to upload CV');
          setMessage('Failed to upload CV');
          return;
        }
      }

      const payload = {
        ...form,
        cvUrl: finalCvUrl,
        preferredLocations: String(form.preferredLocations || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      };
      const res = await fetch(`${API_BASE}/transferhub/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        setMessage('Profile saved successfully!');
        setSelectedCvFile(null);
        // Reload the profile data to show updated information
        const updatedProfile = await res.json();
        setForm({
          profession: updatedProfile.profession || '',
          rank: updatedProfile.rank || '',
          department: updatedProfile.department || '',
          currentLocation: updatedProfile.currentLocation || '',
          preferredLocations: Array.isArray(updatedProfile.preferredLocations) ? updatedProfile.preferredLocations.join(', ') : '',
          qualifications: updatedProfile.qualifications || '',
          majors: updatedProfile.majors || '',
          cvUrl: updatedProfile.cvUrl || ''
        });
        setCvPreview(updatedProfile.cvUrl || '');
      } else {
        const errorData = await res.json();
        setMessage(`Failed to save: ${errorData.error || 'Unknown error'}`);
      }
    } catch (e) {
      setMessage('Failed to save');
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>My Transfer Profile</Typography>
      {message && <Alert sx={{ mb: 2 }}>{message}</Alert>}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField label="Profession" fullWidth value={form.profession} onChange={(e) => setForm({ ...form, profession: e.target.value })} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Rank" fullWidth value={form.rank} onChange={(e) => setForm({ ...form, rank: e.target.value })} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Department" fullWidth value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Current Location" fullWidth value={form.currentLocation} onChange={(e) => setForm({ ...form, currentLocation: e.target.value })} />
        </Grid>
        <Grid item xs={12}>
          <TextField label="Preferred Locations (comma separated)" fullWidth value={form.preferredLocations} onChange={(e) => setForm({ ...form, preferredLocations: e.target.value })} />
        </Grid>
        <Grid item xs={12}>
          <TextField label="Qualifications" fullWidth value={form.qualifications} onChange={(e) => setForm({ ...form, qualifications: e.target.value })} />
        </Grid>
        <Grid item xs={12}>
          <TextField label="Majors (optional)" fullWidth value={form.majors} onChange={(e) => setForm({ ...form, majors: e.target.value })} />
        </Grid>
        <Grid item xs={12}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>CV Upload</label>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setSelectedCvFile(file);
                    setForm({ ...form, cvUrl: '' });
                  }
                }}
                style={{ flex: 1 }}
              />
              <span style={{ fontSize: '14px', color: '#666' }}>or</span>
              <TextField 
                label="CV URL (optional)" 
                fullWidth 
                value={form.cvUrl} 
                onChange={(e) => {
                  setForm({ ...form, cvUrl: e.target.value });
                  setSelectedCvFile(null);
                }}
                style={{ flex: 1 }}
              />
            </div>
            {selectedCvFile && (
              <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
                Selected file: {selectedCvFile.name}
              </div>
            )}
            {cvPreview && !selectedCvFile && (
              <div style={{ marginTop: '8px' }}>
                <a href={cvPreview} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'none' }}>
                  View current CV
                </a>
              </div>
            )}
          </div>
        </Grid>
      </Grid>
      <Button sx={{ mt: 2 }} variant="contained" onClick={handleSave}>Save</Button>
    </Box>
  );
};

export default TransferHubProfile;